import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { toast, Card, ErrorBoundary } from '../components/common';
import { useOfflineAuth } from '../hooks/useOfflineAuth';
import { useShopCatalog, useShopCategories, useCurrencyBalances, usePurchaseItem } from '../hooks/useShop';
import type { ShopCatalogItem } from '../types/shop';
import { WifiOff } from 'lucide-react';
import { getDecorationAssetUrl, getDecorationEmoji } from '../utils/decorationAssets';
import { FEATURES } from '../config/features';

function ShopPageContent() {
  const { isOnline } = useOfflineAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('consumable');
  
  const { data: categoriesData, isLoading: catalogLoading, error: catalogError } = useShopCategories();
  const { data: balancesData } = useCurrencyBalances();
  const purchaseItemMutation = usePurchaseItem();
  
  // Redirect to home if offline (this page requires online)
  useEffect(() => {
    if (!isOnline) {
      toast.error('Shop requires an internet connection');
    }
  }, [isOnline]);
  
  // Show offline message if somehow accessed offline
  if (!isOnline) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto text-center p-8">
            <div className="w-16 h-16 mx-auto bg-yellow-900/20 rounded-full flex items-center justify-center mb-4">
              <WifiOff className="h-8 w-8 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Shop Unavailable Offline
            </h2>
            <p className="text-gray-400 mb-6">
              The shop requires an active internet connection to browse items and make purchases.
              Please connect to the internet and try again.
            </p>
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Return to Dashboard
            </button>
          </Card>
        </div>
      </Layout>
    );
  }
  
  const categoriesData_raw = categoriesData?.data || [];
  
  // Extract all items from categories response
  // Backend returns: { categories: [{ category, itemCount, items: [...] }] }
  const allItems: ShopCatalogItem[] = [];
  const categories: { category: string; count: number }[] = [];
  
  categoriesData_raw.forEach((cat: any) => {
    // Add to categories list
    categories.push({
      category: cat.category,
      count: cat.itemCount || cat.items?.length || 0
    });
    
    // Extract items and transform to ShopCatalogItem format
    if (cat.items && Array.isArray(cat.items)) {
      cat.items.forEach((item: any) => {
        allItems.push({
          id: item.id,
          itemId: item.id,
          price: item.priceCoins || 0,
          currencyId: 1, // Default to coins
          isActive: item.isActive !== false,
          isFeatured: item.isFeatured || false,
          discountPercentage: 0,
          availableFrom: item.availableFrom,
          availableUntil: item.availableUntil,
          maxPurchases: undefined,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          item: {
            id: item.id,
            name: item.itemName,
            description: item.description || '',
            category: item.itemType, // This is the key field for filtering
            rarity: 'common',
            metadata: item.metadata || {},
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
          },
          currency: {
            id: 1,
            name: 'Coins',
            symbol: 'COIN',
            description: 'Game currency',
            isSoft: true,
            metadata: {
              icon: '🪙',
              color: '#FFD700'
            }
          }
        });
      });
    }
  });
  
  const balances = balancesData?.data || [];
  
  // Debug logging
  console.log('🛒 Shop Debug:', {
    totalItems: allItems.length,
    categories: categories,
    selectedCategory,
    sampleItem: allItems[0]
  });
  
  // Filter items by selected category
  // The API returns item.itemType, but our transformation maps it to item.category
  const filteredItems = allItems.filter((item: ShopCatalogItem) => {
    const matches = item.item.category === selectedCategory;
    if (!matches && allItems.indexOf(item) < 3) {
      console.log('❌ Item filtered out:', {
        itemName: item.item.name,
        itemCategory: item.item.category,
        selectedCategory,
        fullItem: item
      });
    }
    return matches;
  });
  
  console.log('✅ Filtered items:', {
    selectedCategory,
    filteredCount: filteredItems.length,
    filteredItems: filteredItems.slice(0, 3)
  });
  
  // Get category display names and emojis
  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'consumable': return { name: 'Reading Packs', emoji: '📚' };
      case 'permanent': return { name: 'Features', emoji: '⚡' };
      case 'currency': return { name: 'Coin Packs', emoji: '💰' };
      case 'decoration': return { name: 'Decoration', emoji: '🎨' };
      case 'deep_dive': return { name: 'Deep Dive', emoji: '🔍' };
      case 'bundle': return { name: 'Bundle', emoji: '🎁' };
      default: return { name: category, emoji: '🎁' };
    }
  };
  
  const getItemEmoji = (item: ShopCatalogItem) => {
    const itemName = item.item.name.toLowerCase();
    const itemType = item.item.category;
    
    // Decoration items - use actual decoration emoji
    if (itemType === 'decoration') {
      const metadata = item.item.metadata as any;
      const decorationType = metadata?.decoration_type || metadata?.category;
      return getDecorationEmoji(decorationType || 'decoration');
    }
    
    // Deep dive tokens
    if (itemType === 'deep_dive' || itemName.includes('deep dive')) {
      return '🔍';
    }
    
    // Bundles
    if (itemType === 'bundle' || itemName.includes('bundle')) {
      return '🎁';
    }
    
    // Reading packs
    if (itemName.includes('basic')) return '📖';
    if (itemName.includes('premium')) return '📘';
    if (itemName.includes('deluxe')) return '📙';
    
    // AI models
    if (itemName.includes('gpt-4o-mini')) return '🤖';
    if (itemName.includes('gpt-4o')) return '🧠';
    
    // Features
    if (itemName.includes('export')) return '📄';
    if (itemName.includes('spread')) return '🔮';
    
    // Coin packs
    if (itemName.includes('small')) return '🪙';
    if (itemName.includes('medium')) return '💰';
    if (itemName.includes('large')) return '💎';
    
    return '🎁';
  };
  
  const getItemImage = (item: ShopCatalogItem) => {
    const itemType = item.item.category;
    
    // For decoration items, get actual asset URL
    if (itemType === 'decoration') {
      try {
        return getDecorationAssetUrl(item.item, true); // Use preview
      } catch (error) {
        console.error('Error getting decoration asset URL:', error);
        return null;
      }
    }
    
    // For other items, return null (will show emoji)
    return null;
  };
  
  const handlePurchase = async (item: ShopCatalogItem) => {
    // Check if user has enough balance
    const userBalance = balances.find((balance: any) => balance.currencyId === item.currencyId);
    if (!userBalance || userBalance.balance < item.price) {
      toast.error(`Insufficient ${userBalance?.currency.name || 'coins'}. You need ${item.price} but only have ${userBalance?.balance || 0}.`);
      return;
    }
    
    // Confirm purchase
    const confirmed = window.confirm(
      `Purchase ${item.item.name} for ${item.price} ${userBalance.currency.name}?`
    );
    
    if (!confirmed) return;
    
    try {
      await purchaseItemMutation.mutateAsync({
        itemId: item.itemId,
        quantity: 1
      });
      
      // Success toast is handled by the mutation hook
      console.log('Purchase successful:', item);
    } catch (error) {
      // Error toast is handled by the mutation hook
      console.error('Purchase failed:', error);
    }
  };
  
  if (catalogLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white">Loading shop...</div>
        </div>
      </Layout>
    );
  }
  
  if (catalogError) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-red-400">Error loading shop: {catalogError.message}</div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* Custom scrollbar styles */}
        <style>{`
          #products-container::-webkit-scrollbar {
            height: 12px;
          }
          #products-container::-webkit-scrollbar-track {
            background: #1F2937;
            border-radius: 6px;
          }
          #products-container::-webkit-scrollbar-thumb {
            background: #4B5563;
            border-radius: 6px;
          }
          #products-container::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
        `}</style>
        
        {/* Top Row - Category Tabs */}
        <div className="border-b border-gray-800">
          <div className="flex justify-center">
            {categories.map((category: { category: string; count: number }) => {
              const categoryInfo = getCategoryInfo(category.category);
              const isSelected = selectedCategory === category.category;
              
              return (
                <button
                  key={category.category}
                  onClick={() => setSelectedCategory(category.category)}
                  className={`px-6 py-4 border-b-2 transition-colors ${
                    isSelected 
                      ? 'border-yellow-500 bg-gray-900 text-yellow-500' 
                      : 'border-transparent hover:bg-gray-900 text-gray-400 hover:text-white'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-lg">{categoryInfo.emoji}</span>
                    <span className="text-sm font-medium">{categoryInfo.name}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Middle Row - Horizontal Scrollable Products */}
        <div className="flex-1 py-8">
          <div className="relative px-8">
            {/* Products Container with visible scrollbar */}
            <div
              id="products-container"
              className="flex gap-4 overflow-x-auto pb-4"
              style={{ 
                scrollbarWidth: 'thin',
                scrollbarColor: '#4B5563 #1F2937'
              }}
            >
              {filteredItems.map((item: ShopCatalogItem) => {
                const itemImage = getItemImage(item);
                
                return (
                <div
                  key={`${item.id}-${item.itemId}`}
                  className="flex-shrink-0 w-64 border border-gray-600 rounded-lg p-4 bg-gray-900 hover:bg-gray-800 transition-colors"
                >
                  {/* Product Image */}
                  <div className="text-center mb-4">
                    {itemImage ? (
                      // Show actual image for decorations
                      <div className="w-full max-w-[200px] mx-auto aspect-[4/7] bg-gray-800 rounded-lg overflow-hidden mb-2">
                        <img
                          src={itemImage}
                          alt={item.item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to emoji if image fails to load
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-6xl">${getItemEmoji(item)}</div>`;
                            }
                          }}
                        />
                      </div>
                    ) : (
                      // Show emoji for non-decoration items
                      <div className="text-6xl mb-2">
                        {getItemEmoji(item)}
                      </div>
                    )}
                  </div>
                  
                  {/* Product Name */}
                  <h3 className="text-lg font-semibold text-center mb-2 text-white">
                    {item.item.name}
                  </h3>
                  
                  {/* Product Description */}
                  <p className="text-sm text-gray-400 text-center mb-4 line-clamp-2">
                    {item.item.description}
                  </p>
                  
                  {/* Product Price */}
                  <div className="text-center mb-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl">🪙</span>
                      <span className="text-xl font-bold text-yellow-500">
                        {item.price}
                      </span>
                    </div>
                    
                    {/* Sale Badge */}
                    {(item.discountPercentage || 0) > 0 && (
                      <div className="mt-2">
                        <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">
                          {item.discountPercentage}% OFF
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Purchase Button */}
                  <button
                    onClick={() => handlePurchase(item)}
                    disabled={purchaseItemMutation.isPending}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      purchaseItemMutation.isPending
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {purchaseItemMutation.isPending ? 'Purchasing...' : 'Purchase'}
                  </button>
                </div>
              );
              })}
              
              {filteredItems.length === 0 && (
                <div className="flex-shrink-0 w-full text-center py-12">
                  <p className="text-gray-400">No items available in this category</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom Row - Available Coins */}
        <div className="border-t border-gray-800 bg-gray-900">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-center gap-6">
              {balances.map((balance: any) => (
                <div key={balance.currencyId} className="flex items-center gap-2">
                  <span className="text-2xl">🪙</span>
                  <span className="text-xl font-bold text-yellow-500">
                    {balance.balance.toLocaleString()}
                  </span>
                </div>
              ))}
              
              {balances.length === 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🪙</span>
                  <span className="text-xl font-bold text-yellow-500">0</span>
                </div>
              )}
            </div>
            
            {/* Hide Buy Coins button when monetization disabled */}
            {FEATURES.ENABLE_MONETIZATION && (
              <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                + Buy Coins
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default function ShopPage() {
  return (
    <ErrorBoundary>
      <ShopPageContent />
    </ErrorBoundary>
  );
}